<template>
  <div class="step-editor-panel">
    <div class="form-group">
      <label class="form-label">步驟名稱</label>
      <input
        :value="step.stepName"
        type="text"
        class="form-control"
        :disabled="disabled"
        @input="patchStep({ stepName: $event.target.value })"
      />
      <div class="step-name-tip hint-box hint-info" role="note">
        <i class="ri-information-line"></i>
        <span>
          請填寫一個<strong>自己能辨識</strong>的步驟名稱，方便在左側流程列表中快速找到此步驟。
          例如：<strong>加入好友</strong>、<strong>加入（綁定）會員</strong>、<strong>購物滿一定金額</strong> 等。
        </span>
      </div>
    </div>

    <p v-if="summary" class="step-summary">{{ summary }}</p>

    <section class="step-section">
      <h4>【何時】觸發</h4>
      <select
        :value="step.triggerType"
        class="form-control"
        :disabled="disabled"
        @change="onTriggerTypeChange($event.target.value)"
      >
        <option v-for="opt in triggerOptions" :key="opt.value" :value="opt.value">
          {{ opt.label }}
        </option>
      </select>
      <small class="form-text">{{ currentTriggerHint }}</small>

      <div v-if="step.triggerType === 'EVENT-501'" class="sub-field">
        <label class="form-label">問卷</label>
        <select
          :value="step.triggerConfig?.questionnaireId || ''"
          class="form-control"
          :disabled="disabled"
          @change="patchTriggerConfig({ questionnaireId: $event.target.value })"
        >
          <option value="">請選擇問卷</option>
          <option
            v-for="item in selectableQuestionnaires(step.triggerConfig?.questionnaireId)"
            :key="item.id"
            :value="item.id"
          >
            {{ item.name }}{{ item.status === 'published' ? '' : '（未發布）' }}
          </option>
        </select>
        <small class="form-text" v-pre>只能選擇已發布的問卷。前一步的 LINE 文字可使用 {{questionnaireUrl}}，系統會帶入這位參與者的問卷連結。</small>
      </div>

      <div v-if="step.triggerType === 'EVENT-101'" class="sub-field">
        <label class="form-label">互動標籤</label>
        <select
          :value="step.triggerConfig?.tagId || ''"
          class="form-control"
          :disabled="disabled"
          @change="patchTriggerConfig({ tagId: $event.target.value })"
        >
          <option value="">請選擇標籤</option>
          <option v-for="tag in manualTags" :key="tag.id" :value="tag.id">{{ tag.tagName }}</option>
        </select>
      </div>

      <div v-if="step.triggerType === 'EVENT-202'" class="sub-field">
        <label class="form-label">等待天數</label>
        <input
          :value="step.triggerConfig?.waitDays ?? 3"
          type="number"
          min="1"
          class="form-control"
          :disabled="disabled"
          @input="patchTriggerConfig({ waitDays: Number($event.target.value) || 1 })"
        />
      </div>

      <div v-if="step.triggerType === 'EVENT-201'" class="sub-field">
        <label class="form-label">執行方式</label>
        <select
          :value="executionMode"
          class="form-control"
          :disabled="disabled"
          @change="onExecutionModeChange($event.target.value)"
        >
          <option value="immediate">進入步驟時立即執行</option>
          <option value="scheduled">指定日期時間</option>
          <option value="delay">進入步驟後等待</option>
        </select>
      </div>

      <div v-if="step.triggerType === 'EVENT-201' && executionMode === 'scheduled'" class="sub-field">
        <label class="form-label">指定觸發時間</label>
        <input
          :value="scheduledAtLocal"
          type="datetime-local"
          class="form-control"
          :disabled="disabled"
          @input="patchTriggerConfig({ scheduledAt: fromDatetimeLocal($event.target.value) })"
        />
        <small class="form-text">到達此時間後，符合條件的參與者將觸發本步驟動作</small>
      </div>

      <div v-if="step.triggerType === 'EVENT-201' && executionMode === 'delay'" class="sub-field">
        <label class="form-label">等待時間</label>
        <div class="wait-duration-row">
          <input
            :value="waitValue"
            type="number"
            min="1"
            class="form-control wait-duration-input"
            :disabled="disabled"
            @input="onWaitValueInput($event.target.value)"
          />
          <select
            :value="waitUnit"
            class="form-control wait-duration-unit"
            :disabled="disabled"
            @change="onWaitUnitChange($event.target.value)"
          >
            <option value="hours">小時</option>
            <option value="days">天</option>
          </select>
        </div>
        <small class="form-text">進入此步驟後，等待指定時間再執行動作。</small>
      </div>

      <div v-if="step.triggerType === 'EVENT-301'" class="sub-field">
        <label class="form-label">滿額門檻（元）</label>
        <input
          :value="step.triggerConfig?.amount ?? step.triggerConfig?.minAmount ?? ''"
          type="number"
          min="0"
          class="form-control"
          :disabled="disabled"
          @input="patchTriggerConfig({ amount: Number($event.target.value) || 0 })"
        />
        <small class="form-text">單筆訂單實付金額達此門檻時觸發</small>
      </div>

      <div v-if="step.triggerType === 'EVENT-303'" class="sub-field product-trigger-field">
        <label class="form-label">符合方式</label>
        <div class="product-match-row">
          <label class="radio-pill">
            <input
              type="radio"
              value="any"
              :checked="productMatchMode === 'any'"
              :disabled="disabled"
              @change="patchTriggerConfig({ productMatchMode: 'any' })"
            />
            包含任一指定品項
          </label>
          <label class="radio-pill">
            <input
              type="radio"
              value="all"
              :checked="productMatchMode === 'all'"
              :disabled="disabled"
              @change="patchTriggerConfig({ productMatchMode: 'all' })"
            />
            需同時包含全部指定品項
          </label>
        </div>

        <template v-if="isWixtarMember">
          <label class="form-label">商品代碼</label>
          <input
            type="text"
            class="form-control"
            :value="skuText"
            :disabled="disabled"
            placeholder="例如 MPK009, MPK010"
            @input="onSkuTextInput($event.target.value)"
          />
          <small class="form-text">
            填 Wixtar 商品代碼，與訂單明細的 SKU 相同（例如 MPK009）。可填多個，以逗號分隔。比對時完全相等、忽略大小寫。請填代碼，不是商品名稱。
            {{ productMatchHint }}
          </small>
        </template>
        <template v-else>
          <label class="form-label">指定商品</label>
          <div class="product-pick-actions">
            <button
              type="button"
              class="btn btn-secondary btn-sm"
              :disabled="disabled"
              @click="showProductPicker = true"
            >
              <i class="ri-shopping-bag-3-line"></i>
              選擇商品／規格
            </button>
            <span class="product-pick-count">已選 {{ selectedTriggerProducts.length }} 項</span>
          </div>
          <ul v-if="selectedTriggerProducts.length" class="product-chip-list">
            <li
              v-for="(row, idx) in selectedTriggerProducts"
              :key="row.variantId || row.productId || idx"
              class="product-chip"
            >
              <span class="product-chip-text">
                <strong>{{ row.productName || '商品' }}</strong>
                <template v-if="row.variantLabel"> · {{ row.variantLabel }}</template>
                <span v-if="row.sku" class="product-chip-sku">（{{ row.sku }}）</span>
              </span>
              <button
                v-if="!disabled"
                type="button"
                class="product-chip-remove"
                :aria-label="'移除「' + (row.productName || '') + '」'"
                @click="removeTriggerProduct(idx)"
              >
                <i class="ri-close-line"></i>
              </button>
            </li>
          </ul>
          <small class="form-text">
            <template v-if="!selectedTriggerProducts.length">請選擇至少一項商品或規格。</template>
            {{ productMatchHint }}
          </small>
        </template>
      </div>
    </section>

    <section class="step-section">
      <h4>【誰符合】條件</h4>
      <label class="condition-toggle">
        <input
          v-model="enableTagFilter"
          type="checkbox"
          :disabled="disabled"
        />
        <span>依標籤進一步篩選（選填）</span>
      </label>
      <p v-if="!enableTagFilter" class="form-text condition-toggle-hint">
        未啟用時，僅依上方觸發事件判斷誰進入此步驟。
      </p>
      <div v-else class="condition-panel">
        <TagMatchTypeToggle
          :model-value="step.conditionGroup?.tagLogic || 'OR'"
          label="標籤邏輯"
          show-at-least-option
          :disabled="disabled"
          @update:model-value="onTagLogicChange"
        />
        <div v-if="(step.conditionGroup?.tagLogic || 'OR') === 'AT_LEAST'" class="sub-field">
          <label class="form-label">至少符合幾個</label>
          <input
            :value="tagMatchCount"
            type="number"
            min="1"
            :max="conditionTagCount || 1"
            class="form-control tag-match-count"
            :disabled="disabled || conditionTagCount < 1"
            @input="onTagMatchCountInput($event.target.value)"
          />
          <small class="form-text">
            <template v-if="conditionTagCount < 1">請先選擇標籤。已選 m 個時，填 n 表示至少符合其中 n 個。</template>
            <template v-else>已選 {{ conditionTagCount }} 個標籤，至少符合其中 {{ tagMatchCount }} 個。</template>
          </small>
        </div>
        <TagSelectorMultiple
          :model-value="step.conditionGroup?.tagIds || []"
          label="限制標籤"
          hint="選擇需符合的會員標籤"
          :disabled="disabled"
          @update:model-value="onConditionTagIds"
        />
      </div>
    </section>

    <section class="step-section actions-section">
      <div class="actions-section-head">
        <div>
          <h4>【然後】動作</h4>
          <p class="actions-section-desc">設定此步驟觸發後要執行的動作（推播、發券、發點等）</p>
        </div>
      </div>

      <div v-if="!step.actions?.length" class="actions-empty">
        <i class="ri-play-list-add-line"></i>
        <p>尚未新增任何動作</p>
        <span>請在下方新增推播、發券或其他動作</span>
      </div>

      <div v-for="(action, idx) in step.actions" :key="idx" class="action-card">
        <div class="action-card-head">
          <span class="action-index">動作 {{ idx + 1 }}</span>
          <select
            :value="action.actionType"
            class="form-control"
            :disabled="disabled"
            @change="updateAction(idx, { actionType: $event.target.value, actionConfig: {} })"
          >
            <option v-for="opt in actionOptions" :key="opt.value" :value="opt.value">
              {{ opt.label }}
            </option>
          </select>
        </div>

        <div v-if="['ACTION-001', 'ACTION-002'].includes(action.actionType)" class="sub-field">
          <TagSelectorMultiple
            :model-value="tagActionIds(action.actionConfig)"
            label="要貼上的標籤"
            hint="可選擇一個或多個標籤"
            :disabled="disabled"
            @update:model-value="onTagActionSelect(idx, $event)"
          />
        </div>

        <div v-if="action.actionType === 'ACTION-004'" class="sub-field">
          <label class="form-label">點數</label>
          <p v-if="participantMode === 'friends'" class="form-text action-restricted-hint">
            全部好友模式不支援派發點數，請改選「會員限定」或移除此動作。
          </p>
          <template v-else>
            <input
              :value="action.actionConfig?.amount ?? action.actionConfig?.points ?? ''"
              type="number"
              min="1"
              class="form-control"
              :disabled="disabled"
              @input="onPointsInput(idx, $event.target.value)"
            />
            <small class="form-text">
              將透過 {{ pointsBenefitLabel }} 會員點數體系發放
            </small>
          </template>
        </div>

        <div v-if="action.actionType === 'ACTION-005'" class="sub-field">
          <label class="form-label">優惠券範本</label>
          <p v-if="participantMode === 'friends'" class="form-text action-guidance-hint">
            全部好友模式僅支援 LOCKLINE 票券（將推播至 LINE）。
          </p>
          <select
            :value="getTemplateId(action.actionConfig)"
            class="form-control"
            :disabled="disabled"
            @change="onCouponSelect(idx, $event.target.value)"
          >
            <option value="">請選擇</option>
            <option v-for="tpl in filteredCouponTemplates" :key="tpl.id" :value="tpl.id">
              {{ tpl.templateName || tpl.name }}
            </option>
          </select>
          <RewardStockWarning :template="filteredCouponTemplates.find((tpl) => tpl.id === getTemplateId(action.actionConfig))" />
        </div>

        <div v-if="action.actionType === 'ACTION-012'" class="sub-field">
          <label class="form-label">Wixtar 票券</label>
          <p v-if="participantMode === 'friends'" class="form-text action-restricted-hint">
            全部好友模式僅對已綁定 Wixtar 的會員發送，未綁定者會略過。
          </p>
          <WixtarTicketMultiSelect
            :model-value="action.actionConfig?.wixtarTicketIds || []"
            :tickets="wixtarTickets"
            :loading="wixtarTicketsLoading"
            @update:model-value="onWixtarTickets(idx, $event)"
          />
          <small class="form-text">可選擇多張票券，會員需已綁定 Wixtar。</small>
        </div>

        <div v-if="action.actionType === 'ACTION-013'" class="sub-field">
          <label class="form-label">問卷</label>
          <select
            :value="action.actionConfig?.questionnaireId || ''"
            class="form-control"
            :disabled="disabled"
            @change="onQuestionnaireActionSelect(idx, $event.target.value)"
          >
            <option value="">請選擇問卷</option>
            <option v-for="item in publishedQuestionnaires(action)" :key="item.id" :value="item.id">
              {{ item.name }}{{ item.status === 'published' ? '' : '（未發布）' }}
            </option>
          </select>
          <label class="form-label">推播文字</label>
          <input
            :value="action.actionConfig?.introText || ''"
            type="text"
            class="form-control"
            placeholder="邀請您填寫問卷："
            :disabled="disabled"
            @input="onQuestionnaireIntro(idx, $event.target.value)"
          />
          <small class="form-text">執行時用 LINE 推播這位參與者專用的填寫連結。下一步若是「完成問卷」且選同一份，填完才會往下走。</small>
        </div>

        <div v-if="action.actionType === 'ACTION-006'" class="sub-field">
          <label class="form-label">抽獎設定</label>
          <select
            :value="getLotteryConfigId(action.actionConfig)"
            class="form-control"
            :disabled="disabled"
            @change="onLotterySelect(idx, $event.target.value)"
          >
            <option value="">請選擇</option>
            <option v-for="cfg in lotteryConfigs" :key="cfg.id" :value="cfg.id">
              {{ formatLotteryOption(cfg) }}
            </option>
          </select>
          <small v-if="!lotteryConfigs.length" class="form-text">
            尚無抽獎設定，請先至「抽獎工具管理」建立。
          </small>
        </div>

        <div v-if="action.actionType === 'ACTION-008'" class="sub-field">
          <label class="form-label">LINE 推播範本</label>
          <select
            :value="getTemplateId(action.actionConfig)"
            class="form-control"
            :disabled="disabled"
            @change="onLineTemplateSelect(idx, $event.target.value)"
          >
            <option value="">請選擇</option>
            <option v-for="tpl in lineTemplates" :key="tpl.id" :value="tpl.id">
              {{ tpl.templateName || tpl.name }}
            </option>
          </select>
        </div>

        <div v-if="!disabled" class="action-card-tools">
          <button
            type="button"
            class="btn btn-sm btn-outline-secondary"
            :disabled="idx === 0"
            title="上移動作"
            @click="moveAction(idx, -1)"
          >
            <i class="ri-arrow-up-line"></i>
            上移
          </button>
          <button
            type="button"
            class="btn btn-sm btn-outline-secondary"
            :disabled="idx === step.actions.length - 1"
            title="下移動作"
            @click="moveAction(idx, 1)"
          >
            <i class="ri-arrow-down-line"></i>
            下移
          </button>
          <button
            type="button"
            class="btn btn-sm btn-outline-danger"
            title="刪除此動作"
            @click.stop="removeAction(idx)"
          >
            <i class="ri-delete-bin-line"></i>
            刪除動作
          </button>
        </div>
      </div>

      <button
        v-if="!disabled"
        type="button"
        class="btn btn-primary btn-add-action"
        @click="addAction"
      >
        <i class="ri-add-line"></i>
        新增動作
      </button>
    </section>
  </div>

  <ProductPickerModal
    :open="showProductPicker"
    title="選擇觸發商品（可多筆）"
    :variant-required="true"
    :stay-open-on-select="true"
    :exclude-product-types="['bundle']"
    @close="showProductPicker = false"
    @select="onTriggerProductPick"
  />
</template>

<script>
import { computed, onMounted, ref, watch } from 'vue';
import { questionnaireService } from '@/api/modules/questionnaires.js';
import { tagService } from '@/api/modules/tags.js';
import TagSelectorMultiple from '@/components/TagSelectorMultiple.vue';
import TagMatchTypeToggle from '@/components/TagMatchTypeToggle.vue';
import WixtarTicketMultiSelect from '@/components/WixtarTicketMultiSelect.vue';
import RewardStockWarning from '@/components/RewardStockWarning.vue';
import ProductPickerModal from '@/components/ProductPickerModal.vue';
import { TRIGGER_OPTIONS, ACTION_OPTIONS, summarizeStep } from '@/constants/marketingCampaign.js';
import { showConfirmDeferred, showWarning } from '@/utils/swal.js';

function toDatetimeLocal(iso) {
  if (!iso) return '';
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return '';
  const pad = (n) => String(n).padStart(2, '0');
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T${pad(d.getHours())}:${pad(d.getMinutes())}`;
}

function fromDatetimeLocal(value) {
  if (!value) return null;
  const d = new Date(value);
  if (Number.isNaN(d.getTime())) return null;
  return d.toISOString();
}

function defaultScheduledAtLocal() {
  const d = new Date();
  d.setHours(d.getHours() + 1, 0, 0, 0);
  return toDatetimeLocal(d.toISOString());
}

export default {
  name: 'MarketingCampaignStepEditor',
  components: { TagSelectorMultiple, TagMatchTypeToggle, ProductPickerModal, WixtarTicketMultiSelect, RewardStockWarning },
  props: {
    step: { type: Object, required: true },
    disabled: { type: Boolean, default: false },
    couponTemplates: { type: Array, default: () => [] },
    lineTemplates: { type: Array, default: () => [] },
    lotteryConfigs: { type: Array, default: () => [] },
    wixtarTickets: { type: Array, default: () => [] },
    wixtarTicketsLoading: { type: Boolean, default: false },
    participantMode: { type: String, default: 'friends' },
    memberSystemType: { type: String, default: 'LOCKLINE' }
  },
  emits: ['update:step'],
  setup(props, { emit }) {
    const triggerOptions = TRIGGER_OPTIONS;
    const actionOptions = computed(() => {
      const hasWixtarAction = (props.step.actions || []).some((action) => action.actionType === 'ACTION-012');
      return ACTION_OPTIONS.filter(
        (opt) => opt.value !== 'ACTION-012' || props.memberSystemType === 'WIXTAR' || hasWixtarAction
      );
    });

    const isLocklineCouponTemplate = (tpl) =>
      tpl?.benefitSource !== 'WIXTAR' && tpl?.integrationType !== 'WIXTAR';

    const filteredCouponTemplates = computed(() =>
      (props.couponTemplates || []).filter(isLocklineCouponTemplate)
    );


    const pointsBenefitLabel = computed(() =>
      props.memberSystemType === 'WIXTAR' ? 'Wixtar' : 'LOCKLINE'
    );

    const resolvePointsBenefitSource = () =>
      props.memberSystemType === 'WIXTAR' ? 'WIXTAR' : 'LOCKLINE';

    const summary = computed(() => summarizeStep(props.step));
    const enableTagFilter = ref(false);
    const questionnaires = ref([]);
    const manualTags = ref([]);
    onMounted(async () => {
      try {
        const response = await questionnaireService.list({ page: 1, limit: 100 });
        questionnaires.value = response?.data?.items || [];
      } catch {
        questionnaires.value = [];
      }
      try {
        const response = await tagService.getTags({ tagSource: 'manual', isActive: true });
        const data = response?.data;
        manualTags.value = Array.isArray(data) ? data : (data?.items || []);
      } catch {
        manualTags.value = [];
      }
    });
    const showProductPicker = ref(false);
    const currentTriggerHint = computed(() =>
      triggerOptions.find((o) => o.value === props.step.triggerType)?.hint || ''
    );

    const isWixtarMember = computed(() => props.memberSystemType === 'WIXTAR');
    const selectedTriggerProducts = computed(() => props.step.triggerConfig?.products || []);
    const skuText = computed(() => {
      const cfg = props.step.triggerConfig || {};
      if (typeof cfg.skuText === 'string') return cfg.skuText;
      return Array.isArray(cfg.skus) ? cfg.skus.join(', ') : '';
    });
    const productMatchMode = computed(() =>
      props.step.triggerConfig?.productMatchMode === 'all' ? 'all' : 'any'
    );
    const productMatchHint = computed(() =>
      productMatchMode.value === 'all'
        ? '訂單完成且同時包含全部指定品項時觸發。'
        : '訂單完成且包含任一指定品項時觸發。'
    );

    watch(
      () => [props.step?.id, props.step?.stepOrder],
      () => {
        enableTagFilter.value = (props.step.conditionGroup?.tagIds?.length || 0) > 0;
      },
      { immediate: true }
    );

    const executionMode = computed(() => {
      const cfg = props.step.triggerConfig || {};
      if (cfg.runOnActivate === true) return 'immediate';
      const waitValue = Number(cfg.waitValue ?? 0);
      if (Number.isFinite(waitValue) && waitValue > 0) return 'delay';
      return 'scheduled';
    });

    const scheduledAtLocal = computed(() =>
      toDatetimeLocal(props.step.triggerConfig?.scheduledAt)
    );

    const waitValue = computed(() => {
      const raw = Number(props.step.triggerConfig?.waitValue ?? 1);
      return Number.isFinite(raw) && raw > 0 ? raw : 1;
    });

    const waitUnit = computed(() =>
      props.step.triggerConfig?.waitUnit === 'hours' ? 'hours' : 'days'
    );

    const emitStep = (next) => emit('update:step', next);

    const patchStep = (patch) => emitStep({ ...props.step, ...patch });

    const patchTriggerConfig = (patch) => {
      emitStep({
        ...props.step,
        triggerConfig: { ...(props.step.triggerConfig || {}), ...patch }
      });
    };

    const patchConditionGroup = (patch) => {
      const base = props.step.conditionGroup || { tagLogic: 'OR', tagIds: [] };
      emitStep({ ...props.step, conditionGroup: { ...base, ...patch } });
    };

    const conditionTagCount = computed(() => (props.step.conditionGroup?.tagIds || []).length);

    const tagMatchCount = computed(() => {
      const raw = Number(props.step.conditionGroup?.tagMatchCount);
      const max = Math.max(conditionTagCount.value, 1);
      if (!Number.isFinite(raw) || raw < 1) return 1;
      return Math.min(Math.floor(raw), max);
    });

    const onTagLogicChange = (tagLogic) => {
      const patch = { tagLogic };
      if (tagLogic === 'AT_LEAST') {
        patch.tagMatchCount = tagMatchCount.value;
      }
      patchConditionGroup(patch);
    };

    const onTagMatchCountInput = (value) => {
      const max = Math.max(conditionTagCount.value, 1);
      const raw = Number(value);
      const next = Number.isFinite(raw) ? Math.min(Math.max(Math.floor(raw), 1), max) : 1;
      patchConditionGroup({ tagLogic: 'AT_LEAST', tagMatchCount: next });
    };

    const onConditionTagIds = (tagIds) => {
      const patch = { tagIds };
      if (props.step.conditionGroup?.tagLogic === 'AT_LEAST') {
        const max = Math.max(tagIds.length, 1);
        patch.tagMatchCount = Math.min(tagMatchCount.value, max);
      }
      patchConditionGroup(patch);
    };

    const onTriggerTypeChange = (triggerType) => {
      let triggerConfig = {};
      if (triggerType === 'EVENT-202') {
        triggerConfig = { waitDays: 3 };
      } else if (triggerType === 'EVENT-201') {
        triggerConfig = { runOnActivate: true, scheduledAt: null, waitValue: null, waitUnit: null };
      } else if (triggerType === 'EVENT-301') {
        triggerConfig = { amount: 0 };
      } else if (triggerType === 'EVENT-303') {
        triggerConfig = isWixtarMember.value
          ? { skus: [], skuText: '', productMatchMode: 'any' }
          : { products: [], productMatchMode: 'any' };
      } else if (triggerType === 'EVENT-501') {
        triggerConfig = { questionnaireId: '' };
      } else if (triggerType === 'EVENT-101') {
        triggerConfig = { tagId: '' };
      }
      emitStep({
        ...props.step,
        triggerType,
        triggerConfig
      });
    };

    const onExecutionModeChange = (mode) => {
      if (mode === 'immediate') {
        patchTriggerConfig({
          runOnActivate: true,
          scheduledAt: null,
          waitValue: null,
          waitUnit: null
        });
        return;
      }
      if (mode === 'delay') {
        patchTriggerConfig({
          runOnActivate: false,
          scheduledAt: null,
          waitValue: waitValue.value,
          waitUnit: waitUnit.value
        });
        return;
      }
      const scheduledAt = props.step.triggerConfig?.scheduledAt || fromDatetimeLocal(defaultScheduledAtLocal());
      patchTriggerConfig({
        runOnActivate: false,
        scheduledAt,
        waitValue: null,
        waitUnit: null
      });
    };

    const onWaitValueInput = (rawValue) => {
      const value = Number(rawValue);
      patchTriggerConfig({
        waitValue: Number.isFinite(value) && value > 0 ? value : 1
      });
    };

    const onWaitUnitChange = (unit) => {
      patchTriggerConfig({
        waitUnit: unit === 'hours' ? 'hours' : 'days'
      });
    };

    const getTemplateId = (cfg = {}) =>
      cfg.templateId || cfg.lineMsgTemplateId || cfg.couponTemplateId || cfg.resolvedBinding?.resourceId || '';

    const getLotteryConfigId = (cfg = {}) =>
      cfg.lotteryConfigId || cfg.templateId || cfg.resolvedBinding?.resourceId || '';

    const lotteryToolLabel = (toolType) => ({
      wheel: '轉盤',
      scratch: '刮刮樂',
      mario: '小瑪利',
      draw: '抽籤',
      scratch_card: '搓搓樂',
      other: '其他'
    }[toolType] || toolType);

    const formatLotteryOption = (cfg) => {
      const name = cfg.configName || cfg.name || cfg.id;
      return cfg.toolType ? `${name}（${lotteryToolLabel(cfg.toolType)}）` : name;
    };

    const normalizeActions = (actions) =>
      actions.map((a, i) => ({ ...a, executionOrder: i + 1 }));

    const updateAction = (idx, patch) => {
      const actions = [...(props.step.actions || [])];
      actions[idx] = { ...actions[idx], ...patch };
      emitStep({ ...props.step, actions: normalizeActions(actions) });
    };

    const addAction = () => {
      const actions = [...(props.step.actions || []), {
        actionType: 'ACTION-008',
        executionOrder: (props.step.actions?.length || 0) + 1,
        actionConfig: { channel: 'line' }
      }];
      emitStep({ ...props.step, actions: normalizeActions(actions) });
    };

    const removeAction = async (idx) => {
      const action = props.step.actions?.[idx];
      const actionLabel = actionOptions.value.find((o) => o.value === action?.actionType)?.label || `動作 ${idx + 1}`;
      const confirmed = await showConfirmDeferred(
        '刪除動作',
        `確定要刪除「${actionLabel}」嗎？`
      );
      if (!confirmed) return;

      const actions = (props.step.actions || []).filter((_, i) => i !== idx);
      emitStep({ ...props.step, actions: normalizeActions(actions) });
    };

    const moveAction = (idx, delta) => {
      const actions = [...(props.step.actions || [])];
      const target = idx + delta;
      if (target < 0 || target >= actions.length) return;
      [actions[idx], actions[target]] = [actions[target], actions[idx]];
      emitStep({ ...props.step, actions: normalizeActions(actions) });
    };

    const onWixtarTickets = (idx, ids) => {
      updateAction(idx, {
        actionType: 'ACTION-012',
        actionConfig: {
          ...(props.step.actions?.[idx]?.actionConfig || {}),
          wixtarTicketIds: ids
        }
      });
    };

    const onCouponSelect = (idx, templateId) => {
      updateAction(idx, {
        actionConfig: {
          templateId,
          benefitSource: 'LOCKLINE',
          resolvedBinding: templateId ? { resourceType: 'coupon_template', resourceId: templateId } : null
        }
      });
    };

    const onPointsInput = (idx, rawValue) => {
      updateAction(idx, {
        actionConfig: {
          ...props.step.actions?.[idx]?.actionConfig,
          amount: Number(rawValue) || 0,
          benefitSource: resolvePointsBenefitSource()
        }
      });
    };

    const onLineTemplateSelect = (idx, templateId) => {
      updateAction(idx, {
        actionConfig: {
          channel: 'line',
          lineMsgTemplateId: templateId,
          templateId,
          resolvedBinding: templateId ? { resourceType: 'line_msg_template', resourceId: templateId } : null
        }
      });
    };

    const selectableQuestionnaires = (selectedId) => questionnaires.value.filter(
      (item) => item.status === 'published' || (selectedId && item.id === selectedId)
    );

    const publishedQuestionnaires = (action) => selectableQuestionnaires(action?.actionConfig?.questionnaireId);

    const onQuestionnaireActionSelect = (idx, questionnaireId) => {
      updateAction(idx, {
        actionConfig: {
          ...(props.step.actions?.[idx]?.actionConfig || {}),
          questionnaireId,
          resolvedBinding: questionnaireId
            ? { resourceType: 'questionnaire', resourceId: questionnaireId }
            : null
        }
      });
    };

    const onQuestionnaireIntro = (idx, introText) => {
      updateAction(idx, {
        actionConfig: {
          ...(props.step.actions?.[idx]?.actionConfig || {}),
          introText
        }
      });
    };

    const onLotterySelect = (idx, lotteryConfigId) => {
      updateAction(idx, {
        actionConfig: {
          lotteryConfigId,
          resolvedBinding: lotteryConfigId
            ? { resourceType: 'lottery_config', resourceId: lotteryConfigId }
            : null
        }
      });
    };

    const tagActionIds = (cfg = {}) => {
      if (Array.isArray(cfg.tagIds) && cfg.tagIds.length) return cfg.tagIds;
      if (cfg.tagId) return [cfg.tagId];
      return [];
    };

    const formatVariantPickLabel = (variant) => {
      if (variant?.attributes && Object.keys(variant.attributes).length > 0) {
        return Object.values(variant.attributes).filter(Boolean).join(' / ');
      }
      return variant?.sku || '規格';
    };

    const syncTriggerProducts = (products) => {
      patchTriggerConfig({
        products,
        productIds: [...new Set(products.map((p) => p.productId).filter(Boolean))],
        variantIds: [...new Set(products.map((p) => p.variantId).filter(Boolean))]
      });
    };

    const onTriggerProductPick = ({ product, variant }) => {
      if (!variant?.id) return;
      const variantId = String(variant.id);
      const list = [...selectedTriggerProducts.value];
      if (list.some((row) => String(row.variantId) === variantId)) {
        void showWarning('已選過', '此規格已在清單中。');
        return;
      }
      list.push({
        productId: product?.id ? String(product.id) : null,
        variantId,
        productName: product?.name || '',
        variantLabel: formatVariantPickLabel(variant),
        sku: variant.sku || ''
      });
      syncTriggerProducts(list);
    };

    const removeTriggerProduct = (index) => {
      const list = selectedTriggerProducts.value.filter((_, i) => i !== index);
      syncTriggerProducts(list);
    };

    const onSkuTextInput = (value) => {
      const skus = [...new Set(
        String(value || '')
          .split(/[,，]/)
          .map((sku) => sku.trim())
          .filter(Boolean)
      )];
      patchTriggerConfig({ skuText: value, skus });
    };

    const onTagActionSelect = (idx, tagIds) => {
      updateAction(idx, {
        actionType: 'ACTION-002',
        actionConfig: {
          ...(props.step.actions?.[idx]?.actionConfig || {}),
          tagIds,
          tagId: null
        }
      });
    };

    return {
      triggerOptions,
      actionOptions,
      summary,
      enableTagFilter,
      conditionTagCount,
      tagMatchCount,
      onTagLogicChange,
      onTagMatchCountInput,
      onConditionTagIds,
      questionnaires,
      manualTags,
      showProductPicker,
      isWixtarMember,
      skuText,
      selectedTriggerProducts,
      productMatchMode,
      productMatchHint,
      currentTriggerHint,
      executionMode,
      scheduledAtLocal,
      waitValue,
      waitUnit,
      fromDatetimeLocal,
      patchStep,
      patchTriggerConfig,
      patchConditionGroup,
      onTriggerTypeChange,
      onSkuTextInput,
      onExecutionModeChange,
      onWaitValueInput,
      onWaitUnitChange,
      getTemplateId,
      getLotteryConfigId,
      lotteryToolLabel,
      formatLotteryOption,
      updateAction,
      addAction,
      removeAction,
      moveAction,
      onCouponSelect,
      onWixtarTickets,
      onPointsInput,
      onLineTemplateSelect,
      onLotterySelect,
      publishedQuestionnaires,
      selectableQuestionnaires,
      onQuestionnaireActionSelect,
      onQuestionnaireIntro,
      onTriggerProductPick,
      removeTriggerProduct,
      tagActionIds,
      onTagActionSelect,
      filteredCouponTemplates,
      pointsBenefitLabel,
      participantMode: computed(() => props.participantMode)
    };
  }
};
</script>

<style scoped>
.step-editor-panel {
  display: flex;
  flex-direction: column;
  gap: 12px;
}

.step-summary {
  margin: 0;
  padding: 10px 12px;
  background: #f8f9fa;
  border-radius: 6px;
  font-size: 13px;
  color: #495057;
}

.step-section {
  padding-top: 12px;
  border-top: 1px solid #f0f0f0;
}

.step-section h4 {
  margin: 0 0 10px;
  font-size: 14px;
  color: #495057;
}

.condition-toggle {
  display: flex;
  align-items: center;
  gap: 8px;
  font-size: 14px;
  color: #374151;
  cursor: pointer;
  user-select: none;
}

.condition-toggle input {
  width: 16px;
  height: 16px;
  cursor: pointer;
}

.condition-toggle-hint {
  margin-top: 6px;
}

.condition-panel {
  margin-top: 12px;
  padding: 12px;
  background: #f8f9fa;
  border: 1px solid #e9ecef;
  border-radius: 8px;
  display: flex;
  flex-direction: column;
  gap: 12px;
}

.tag-match-count {
  max-width: 120px;
}

.actions-section {
  padding: 16px;
  background: #f8fafc;
  border: 1px solid #dbeafe;
  border-radius: 10px;
}

.actions-section-head h4 {
  margin-bottom: 4px;
}

.actions-section-desc {
  margin: 0;
  font-size: 13px;
  color: #64748b;
}

.actions-empty {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 4px;
  padding: 24px 16px;
  margin-bottom: 12px;
  border: 2px dashed #cbd5e1;
  border-radius: 8px;
  background: #fff;
  color: #64748b;
  text-align: center;
}

.actions-empty i {
  font-size: 28px;
  color: #94a3b8;
  margin-bottom: 4px;
}

.actions-empty p {
  margin: 0;
  font-size: 14px;
  font-weight: 500;
  color: #475569;
}

.actions-empty span {
  font-size: 12px;
}

.sub-field {
  margin-top: 10px;
}

.checkbox-row label {
  display: flex;
  align-items: center;
  gap: 8px;
  font-size: 14px;
}

.actions-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 8px;
}

.action-card {
  border: 1px solid #e2e8f0;
  border-radius: 8px;
  padding: 14px;
  margin-bottom: 12px;
  background: #fff;
  box-shadow: 0 1px 2px rgba(15, 23, 42, 0.04);
}

.action-card-head {
  display: flex;
  flex-direction: column;
  gap: 8px;
}

.action-index {
  font-size: 12px;
  font-weight: 600;
  color: #64748b;
  text-transform: uppercase;
  letter-spacing: 0.02em;
}

.action-card-tools {
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
  margin-top: 12px;
  padding-top: 12px;
  border-top: 1px solid #f1f5f9;
}

.btn-add-action {
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 6px;
  width: 100%;
  padding: 12px 16px;
  font-size: 15px;
  font-weight: 600;
  border: 2px dashed #93c5fd;
  background: #eff6ff;
  color: #1d4ed8;
}

.btn-add-action:hover {
  background: #dbeafe;
  border-color: #60a5fa;
}

.btn-add-action i {
  font-size: 18px;
}

.form-text {
  display: block;
  margin-top: 4px;
  font-size: 12px;
  color: #6c757d;
}

.action-restricted-hint {
  color: #b91c1c;
  background: #fee2e2;
  border: 1px solid #ef4444;
  border-radius: 6px;
  padding: 8px 10px;
  font-weight: 600;
}

.action-guidance-hint {
  color: #1e3a8a;
  background: #eff6ff;
  border: 1px solid #bfdbfe;
  border-radius: 6px;
  padding: 8px 10px;
}

.step-name-tip {
  margin-top: 10px;
  line-height: 1.5;
}

.hint-box {
  display: flex;
  align-items: flex-start;
  gap: 10px;
  padding: 12px 14px;
  border-radius: 8px;
  font-size: 13px;
}

.hint-box i {
  font-size: 18px;
  flex-shrink: 0;
  margin-top: 1px;
}

.hint-info {
  background: #eff6ff;
  color: #1e40af;
  border: 1px solid #bfdbfe;
}

.hint-info strong {
  color: #1d4ed8;
  font-weight: 600;
}

.product-match-row {
  display: flex;
  flex-wrap: wrap;
  gap: 10px;
  margin-bottom: 12px;
}

.radio-pill {
  display: inline-flex;
  align-items: center;
  gap: 8px;
  padding: 8px 12px;
  border: 1px solid #e2e8f0;
  border-radius: 8px;
  background: #fff;
  font-size: 13px;
  color: #334155;
  cursor: pointer;
  user-select: none;
}

.radio-pill input {
  margin: 0;
}

.product-pick-actions {
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: 10px;
}

.product-pick-count {
  font-size: 13px;
  color: #64748b;
}

.product-chip-list {
  list-style: none;
  margin: 10px 0 0;
  padding: 0;
  display: flex;
  flex-direction: column;
  gap: 8px;
}

.product-chip {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 8px;
  padding: 8px 10px;
  background: #fff;
  border: 1px solid #e2e8f0;
  border-radius: 8px;
}

.product-chip-text {
  font-size: 13px;
  color: #334155;
}

.product-chip-sku {
  color: #64748b;
}

.product-chip-remove {
  border: none;
  background: transparent;
  color: #94a3b8;
  cursor: pointer;
  padding: 2px;
}

.product-chip-remove:hover {
  color: #ef4444;
}

.wait-duration-row {
  display: flex;
  gap: 10px;
}

.wait-duration-input {
  flex: 1 1 auto;
}

.wait-duration-unit {
  width: 120px;
  flex: 0 0 120px;
}
</style>
